/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react';

/**
 * Manages a copy of an object of a defined type that may be edited.
 * @param object The current object value. This will be copied either on mount
 *     or when it becomes non-null/non-unndefined to provide the contents of the
 *     object for editing. The type T will be automatically picked up based on
 *     the type of this argument.
 * @param editableKeys This should be a tuple of strings, holding a list of the
 *     type's properties that can be edited. The returned `editedObject` will only
 *     contain properties of the type T that are listed here. This also provides type
 *     safety for the user of `useEditableStateOfType`. I.e. only the properties
 *     listed here will be available for use on the `editedObject` or as inputs to
 *     `updateProperty` or `getOnChangeTextPropertyUpdater`. This also prevents having
 *     to copy over the full object each time.
 * @returns An object with the following properties:
 *     - `editedObject`: A subset of the original object that reflects its current
 *         edited state. It only contains the properties listed in `editableKeys`.
 *     - `touchedObject`: A subset of the original object that reflects which properties
 *         have been touched. Each property value is a boolean representing whether it has
 *         been touched / modified. It only contains the properties listed in `editableKeys`.
 *     - `updateProperty`: A function that can be called to set a new value for a property.
 *     - `updateProperties`: Sets multiple properties at the same time.
 *     - `getOnChangeTextPropertyUpdater`: A function that when called produces a callback
 *         function that can be pased directly to an `Input` component's `onChangeText`
 *         prop.
 */
const useEditableStateOfType = <T, K extends keyof NonNullable<T>>(
  object: T,
  editableKeys: K[],
): {
  editedObject: Pick<NonNullable<T>, K>;
  touchedObject: { [P in K]: boolean };
  updateProperty: (propertyName: K, newValue: NonNullable<T>[K]) => void;
  updateProperties: (properties: Pick<NonNullable<T>, K>) => void;
  getOnChangeTextPropertyUpdater: (
    propertyName: K,
  ) => (value: NonNullable<T>[K]) => void;
} => {
  const hasSetInitialValues = useRef(false);
  const [editedObject, setEditedObject] = useState<Pick<NonNullable<T>, K>>(
    () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const initialEmptyObject: any = {};
      editableKeys.forEach((key) => {
        initialEmptyObject[key] = null;
      });
      return initialEmptyObject;
    },
  );
  const [touchedObject, setTouchedObject] = useState<{ [P in K]: boolean }>(
    () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const initialEmptyObject: any = {};
      editableKeys.forEach((key) => {
        initialEmptyObject[key] = false;
      });
      return initialEmptyObject;
    },
  );
  useEffect(() => {
    if (
      object !== null &&
      object !== undefined &&
      hasSetInitialValues.current === false
    ) {
      // Copy over only the selected 'editable' keys from the Company object
      // the first time it is defined.
      // no-explicit-any disabled to allow a simple intermediate value
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const partialObject: any = {};
      editableKeys.forEach((key): void => {
        partialObject[key] = (object as NonNullable<T>)[key];
      });
      setEditedObject(partialObject);
      hasSetInitialValues.current = true;
    }
  }, [editableKeys, object]);

  const updateProperty: (
    propertyName: K,
    newValue: NonNullable<T>[K],
  ) => void = (propertyName, newValue) => {
    setEditedObject({
      ...editedObject,
      [propertyName]: newValue,
    });
    setTouchedObject({
      ...touchedObject,
      [propertyName]: true,
    });
  };
  const updateProperties: (properties: Pick<NonNullable<T>, K>) => void = (
    properties,
  ) => {
    setEditedObject({
      ...editedObject,
      ...properties,
    });
  };
  const getOnChangeTextPropertyUpdater: (
    propertyName: K,
  ) => (value: NonNullable<T>[K]) => void = (propertyName) => {
    return (value): void => updateProperty(propertyName, value);
  };

  return {
    editedObject,
    getOnChangeTextPropertyUpdater,
    touchedObject,
    updateProperties,
    updateProperty,
  };
};

export default useEditableStateOfType;
